// store/slices/monitoringSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
    ContainerApp,
    Alert,
    BulkToggleResponse,
    ApiResponse,
    KubernetesClusters,
    KubernetesWorkloads
} from '../../types/monitoring';
import * as monitoringApi from '../../api/monitoringApi';

interface AKSCluster {
    name: string;
    resourceGroup: string;
    location: string;
    kubernetesVersion: string;
    provisioningState: string;
    powerState: string;
    fqdn: string;
    nodeCount: number;
    readyNodes: number;
    healthStatus: string;
    cpuUsage: number;
    memoryUsage: number;
    id: string;
    tags: Record<string, string>;
}

export interface PerformanceData {
    time: string;
    cpu: number;
    memory: number;
    timestamp: string;
}

export interface DaprComponent {
    name: string;
    cluster: string;
    namespace: string;
    type: string;
    status: string;
}

interface MonitoringState {
    selectedAKSCluster: AKSCluster | null;
    aksClusters: AKSCluster[];
    containerapps: ContainerApp[];
    kubernetesClusters: KubernetesClusters[];
    performanceData: PerformanceData[];
    kubernetesApps: KubernetesWorkloads[];
    daprComponents: DaprComponent[];
    currentContainerApp: ContainerApp | null;
    alerts: Alert[];
    loading: boolean;
    bulkActionLoading: boolean;
    selected: string[];
    error: string | null;
    // ========== NEW K8S EXPLORER STATE ========== //
    explorerNamespaces: any[];
    explorerServices: any[];
    explorerAllNamespaces: any[];
    explorerAllServices: any[];
    explorerLoading: boolean;
    explorerError: string | null;
    selectedExplorerCluster: string | null;
    selectedExplorerNamespace: string | null;
}

// Helper functions for localStorage
const loadPersistedCluster = (): AKSCluster | null => {
    try {
        const stored = localStorage.getItem('selectedAKSCluster');
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (error) {
        console.error('Failed to parse stored AKS cluster:', error);
        localStorage.removeItem('selectedAKSCluster');
    }
    return null;
};

const persistCluster = (cluster: AKSCluster | null): void => {
    try {
        if (cluster) {
            localStorage.setItem('selectedAKSCluster', JSON.stringify(cluster));
        } else {
            localStorage.removeItem('selectedAKSCluster');
        }
    } catch (error) {
        console.error('Failed to persist AKS cluster:', error);
    }
};

const initialState: MonitoringState = {
    selectedAKSCluster: loadPersistedCluster(),
    aksClusters: [],
    containerapps: [],
    kubernetesClusters: [],
    performanceData: [],
    kubernetesApps: [],
    daprComponents: [],
    currentContainerApp: null,
    alerts: [],
    loading: false,
    bulkActionLoading: false,
    selected: [],
    error: null,
    // ========== NEW K8S EXPLORER STATE ========== //
    explorerNamespaces: [],
    explorerServices: [],
    explorerAllNamespaces: [],
    explorerAllServices: [],
    explorerLoading: false,
    explorerError: null,
    selectedExplorerCluster: null,
    selectedExplorerNamespace: null,
};

// ========== THUNKS ==========

// Fetch performance data
export const fetchPerformanceData = createAsyncThunk(
    'monitoring/fetchPerformanceData',
    async (clusterName: string | undefined, { rejectWithValue }) => {
        try {
            console.log('Fetching performance data for cluster:', clusterName || 'all');
            const response = await monitoringApi.getPerformanceData(clusterName);
            console.log("response", response);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch performance data');
        }
    }
);

// Fetch AKS clusters
export const fetchAKSClusters = createAsyncThunk(
    'monitoring/fetchAKSClusters',
    async (_, { rejectWithValue }) => {
        try {
            console.log('Fetching AKS clusters');
            const response = await monitoringApi.getAllAKSClusters();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error || 'Failed to fetch AKS clusters');
        }
    }
);


// Fetch cluster apps (workloads)
export const fetchClusterApps = createAsyncThunk(
    'monitoring/fetchClusterApps',
    async (clusterName: string | undefined, { rejectWithValue }) => {
        try {
            console.log('Fetching apps for cluster:', clusterName || 'all');
            const response = await monitoringApi.getClusterApps(clusterName);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch cluster apps');
        }
    }
);

// Fetch Dapr components
export const fetchDaprComponents = createAsyncThunk(
    'monitoring/fetchDaprComponents',
    async (clusterName: string | undefined, { rejectWithValue }) => {
        try {
            console.log('Fetching Dapr components for cluster:', clusterName || 'all');
            const response = await monitoringApi.getDaprComponents(clusterName);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch Dapr components');
        }
    }
);

// Existing thunks
export const fetchWorkloads = createAsyncThunk(
    'monitoring/fetchWorkloads',
    async (_, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.getClusterWorkloads();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch workloads');
        }
    }
);

export const fetchContainerApps = createAsyncThunk(
    'monitoring/fetchContainerApps',
    async (_, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.getContainerApps() as any;
            return {
                containerapps: response.containerApps || [],
                alerts: response.alerts || []
            };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch container apps');
        }
    }
);

export const fetchContainerAppDetails = createAsyncThunk(
    'monitoring/fetchContainerAppDetails',
    async (data: { resourceGroup: string, appName: string }, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.getContainerAppDetails(data.resourceGroup, data.appName);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch container app details');
        }
    }
);

export const restartContainerApp = createAsyncThunk(
    'monitoring/restartContainerApp',
    async (data: { resourceGroup: string, appName: string }, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.restartContainerApp(data.resourceGroup, data.appName);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to restart container app');
        }
    }
);

export const fetchAlerts = createAsyncThunk(
    'monitoring/fetchAlerts',
    async (_, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.getAlerts();
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch alerts');
        }
    }
);

export const acknowledgeAlert = createAsyncThunk(
    'monitoring/acknowledgeAlert',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.acknowledgeAlert(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to acknowledge alert');
        }
    }
);

export const toggleMinScale = createAsyncThunk(
    'monitoring/toggleMinScale',
    async (data: { resourceGroup: string, appName: string }, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.toggleMinScale(data.resourceGroup, data.appName);
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to toggle always active state');
        }
    }
);

export const bulkToggleMinScale = createAsyncThunk<
    ApiResponse<BulkToggleResponse>,
    {
        apps: Array<{ resourceGroup: string, appName: string, id: string }>,
        targetState?: boolean
    },
    { rejectValue: string }
>(
    'monitoring/bulkToggleMinScale',
    async (data, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.bulkToggleMinScale(
                data.apps.map(app => ({ resourceGroup: app.resourceGroup, appName: app.appName })),
                data.targetState
            );
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to perform bulk action');
        }
    }
);

// ========== NEW THUNKS FOR K8S EXPLORER ========== //

export const fetchNamespacesForCluster = createAsyncThunk(
    'monitoring/fetchNamespacesForCluster',
    async (clusterName: string, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.getNamespacesForCluster(clusterName);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch namespaces');
        }
    }
);

export const fetchServicesForClusterNamespace = createAsyncThunk(
    'monitoring/fetchServicesForClusterNamespace',
    async (params: { clusterName: string, namespace: string }, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.getServicesForClusterNamespace(params.clusterName, params.namespace);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch services');
        }
    }
);

export const fetchAllNamespacesAcrossClusters = createAsyncThunk(
    'monitoring/fetchAllNamespacesAcrossClusters',
    async (_, { rejectWithValue }) => {
        try {
            console.log("fetching all namespaces across clusters"); 
            const response = await monitoringApi.getAllNamespacesAcrossClusters();
            console.log("Fetched all namespaces across clusters:", response.data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch all namespaces');
        }
    }
);

export const fetchAllServicesAcrossClusters = createAsyncThunk(
    'monitoring/fetchAllServicesAcrossClusters',
    async (_, { rejectWithValue }) => {
        try {
            const response = await monitoringApi.getAllServicesAcrossClusters();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch all services');
        }
    }
);

// ========== SLICE ==========

const monitoringSlice = createSlice({
    name: 'monitoring',
    initialState,
    reducers: {
        setSelectedAKSCluster: (state, action: PayloadAction<AKSCluster | null>) => {
            state.selectedAKSCluster = action.payload;
            persistCluster(action.payload);
            console.log('Selected AKS Cluster updated:', action.payload?.name || 'cleared');
        },
        loadSelectedAKSCluster: (state) => {
            const stored = loadPersistedCluster();
            if (stored) {
                state.selectedAKSCluster = stored;
                console.log('Loaded persisted AKS Cluster:', stored.name);
            }
        },
        clearError: (state) => {
            state.error = null;
        },
        clearCurrentApp: (state) => {
            state.currentContainerApp = null;
        },
        setFilterStatus: (state, action: PayloadAction<string>) => {
            // Placeholder for filter state
        },
        toggleSelectApp: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            if (state.selected.includes(id)) {
                state.selected = state.selected.filter(appId => appId !== id);
            } else {
                state.selected.push(id);
            }
        },
        selectAllApps: (state) => {
            state.selected = state.containerapps.map(app => app.id);
        },
        clearSelection: (state) => {
            state.selected = [];
        },
        // ========== NEW REDUCERS FOR K8S EXPLORER ========== //
        setSelectedExplorerCluster: (state, action: PayloadAction<string | null>) => {
            state.selectedExplorerCluster = action.payload;
            state.selectedExplorerNamespace = null;
        },
        setSelectedExplorerNamespace: (state, action: PayloadAction<string | null>) => {
            state.selectedExplorerNamespace = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Fetch Performance Data
        builder
            .addCase(fetchPerformanceData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPerformanceData.fulfilled, (state, action) => {
                state.loading = false;
                state.performanceData = action.payload;
            })
            .addCase(fetchPerformanceData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch AKS Clusters
        builder
            .addCase(fetchAKSClusters.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAKSClusters.fulfilled, (state, action) => {
                state.loading = false;
                state.aksClusters = action.payload;

                // Validate selected cluster still exists
                if (state.selectedAKSCluster) {
                    const stillExists = action.payload?.some(
                        (cluster: AKSCluster) => cluster.name === state.selectedAKSCluster?.name
                    );
                    if (!stillExists) {
                        console.log('Selected cluster no longer exists, clearing selection');
                        state.selectedAKSCluster = null;
                        persistCluster(null);
                    }
                }
            })
            .addCase(fetchAKSClusters.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });


        // Fetch Cluster Apps
        builder
            .addCase(fetchClusterApps.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClusterApps.fulfilled, (state, action: PayloadAction<KubernetesWorkloads[]>) => {
                state.loading = false;
                state.kubernetesApps = action.payload;
            })
            .addCase(fetchClusterApps.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Dapr Components
        builder
            .addCase(fetchDaprComponents.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDaprComponents.fulfilled, (state, action: PayloadAction<DaprComponent[]>) => {
                state.loading = false;
                state.daprComponents = action.payload;
            })
            .addCase(fetchDaprComponents.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Workloads
        builder
            .addCase(fetchWorkloads.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWorkloads.fulfilled, (state, action: PayloadAction<KubernetesWorkloads[]>) => {
                state.loading = false;
                state.kubernetesApps = action.payload;
            })
            .addCase(fetchWorkloads.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Container Apps
        builder
            .addCase(fetchContainerApps.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchContainerApps.fulfilled, (state, action) => {
                state.containerapps = action.payload.containerapps;
                state.alerts = action.payload.alerts;
                state.loading = false;
            })
            .addCase(fetchContainerApps.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Container App Details
        builder
            .addCase(fetchContainerAppDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchContainerAppDetails.fulfilled, (state, action) => {
                const containerData = action.payload.data ? action.payload.data : action.payload;
                state.currentContainerApp = containerData as ContainerApp;
                state.loading = false;
            })
            .addCase(fetchContainerAppDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Restart Container App
        builder
            .addCase(restartContainerApp.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(restartContainerApp.fulfilled, (state, action) => {
                const containerData = action.payload.data ? action.payload.data : action.payload;
                state.currentContainerApp = containerData as ContainerApp;
                state.loading = false;
            })
            .addCase(restartContainerApp.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Toggle Min Scale
        builder
            .addCase(toggleMinScale.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleMinScale.fulfilled, (state, action) => {
                const containerData = action.payload.data ? action.payload.data : action.payload;
                const updatedApp = containerData as ContainerApp;

                const index = state.containerapps.findIndex(app => app.id === updatedApp.id);
                if (index !== -1) {
                    state.containerapps[index] = updatedApp;
                }

                if (state.currentContainerApp && state.currentContainerApp.id === updatedApp.id) {
                    state.currentContainerApp = updatedApp;
                }

                state.loading = false;
            })
            .addCase(toggleMinScale.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Bulk Toggle Min Scale
        builder
            .addCase(bulkToggleMinScale.pending, (state) => {
                state.bulkActionLoading = true;
                state.error = null;
            })
            .addCase(bulkToggleMinScale.fulfilled, (state, action) => {
                const bulkResults = action.payload.data.results;

                bulkResults.forEach((result: { id: string; minReplicas: any; isAlwaysActive: any; status: any; }) => {
                    const index = state.containerapps.findIndex(app => app.id === result.id);
                    if (index !== -1) {
                        state.containerapps[index] = {
                            ...state.containerapps[index],
                            minReplicas: result.minReplicas,
                            isAlwaysActive: result.isAlwaysActive,
                            status: result.status
                        };
                    }
                });

                if (state.currentContainerApp) {
                    const updatedCurrentApp = bulkResults.find((app: { id: string | undefined; }) =>
                        app.id === state.currentContainerApp?.id
                    );

                    if (updatedCurrentApp) {
                        state.currentContainerApp = {
                            ...state.currentContainerApp,
                            minReplicas: updatedCurrentApp.minReplicas,
                            isAlwaysActive: updatedCurrentApp.isAlwaysActive,
                            status: updatedCurrentApp.status
                        };
                    }
                }

                state.bulkActionLoading = false;
            })
            .addCase(bulkToggleMinScale.rejected, (state, action) => {
                state.bulkActionLoading = false;
                state.error = action.payload as string;
            });

        // Fetch Alerts
        builder
            .addCase(fetchAlerts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAlerts.fulfilled, (state, action) => {
                const alertsData = action.payload.data ? action.payload.data : action.payload;
                state.alerts = alertsData as Alert[];
                state.loading = false;
            })
            .addCase(fetchAlerts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Acknowledge Alert
        builder
            .addCase(acknowledgeAlert.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(acknowledgeAlert.fulfilled, (state, action) => {
                const alertIndex = state.alerts.findIndex(alert => alert.id === action.payload.id);
                if (alertIndex !== -1) {
                    state.alerts[alertIndex] = action.payload;
                }
                state.loading = false;
            })
            .addCase(acknowledgeAlert.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Namespaces for Cluster
        builder
            .addCase(fetchNamespacesForCluster.pending, (state) => {
                state.explorerLoading = true;
                state.explorerError = null;
            })
            .addCase(fetchNamespacesForCluster.fulfilled, (state, action) => {
                state.explorerLoading = false;
                state.explorerNamespaces = action.payload;
            })
            .addCase(fetchNamespacesForCluster.rejected, (state, action) => {
                state.explorerLoading = false;
                state.explorerError = action.payload as string;
            });

        // Fetch Services for Cluster/Namespace
        builder
            .addCase(fetchServicesForClusterNamespace.pending, (state) => {
                state.explorerLoading = true;
                state.explorerError = null;
            })
            .addCase(fetchServicesForClusterNamespace.fulfilled, (state, action) => {
                state.explorerLoading = false;
                state.explorerServices = action.payload;
            })
            .addCase(fetchServicesForClusterNamespace.rejected, (state, action) => {
                state.explorerLoading = false;
                state.explorerError = action.payload as string;
            });

        // Fetch All Namespaces Across Clusters
        builder
            .addCase(fetchAllNamespacesAcrossClusters.pending, (state) => {
                console.log("Fetching all namespaces across clusters - pending");
                state.explorerLoading = true;
                state.explorerError = null;
            })
            .addCase(fetchAllNamespacesAcrossClusters.fulfilled, (state, action) => {
                console.log("Fetched all namespaces across clusters - fulfilled", action.payload);
                state.explorerLoading = false;
                state.explorerAllNamespaces = action.payload;
            })
            .addCase(fetchAllNamespacesAcrossClusters.rejected, (state, action) => {
                console.log("Failed to fetch all namespaces across clusters - rejected", action.payload);
                state.explorerLoading = false;
                state.explorerError = action.payload as string;
            });

        // Fetch All Services Across Clusters
        builder
            .addCase(fetchAllServicesAcrossClusters.pending, (state) => {
                state.explorerLoading = true;
                state.explorerError = null;
            })
            .addCase(fetchAllServicesAcrossClusters.fulfilled, (state, action) => {
                state.explorerLoading = false;
                state.explorerAllServices = action.payload;
            })
            .addCase(fetchAllServicesAcrossClusters.rejected, (state, action) => {
                state.explorerLoading = false;
                state.explorerError = action.payload as string;
            });
    }
});

export const {
    clearCurrentApp,
    setFilterStatus,
    toggleSelectApp,
    selectAllApps,
    clearSelection,
    setSelectedAKSCluster,
    loadSelectedAKSCluster,
    clearError,
    setSelectedExplorerCluster,
    setSelectedExplorerNamespace
} = monitoringSlice.actions;

export default monitoringSlice.reducer;